import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { 
  UsersIcon, 
  BookOpenIcon, 
  AcademicCapIcon, 
  CalendarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
  BellIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  StarIcon,
  GlobeAsiaAustraliaIcon // 新增图标
} from '@heroicons/react/24/outline'

interface DashboardStats {
  totalUsers: number
  totalTeachers: number
  totalCourses: number
  totalBookings: number
  userGrowth: number
  teacherGrowth: number
  courseGrowth: number
  bookingGrowth: number
  pendingTeacherApplications: number
  newBookingsToday: number
  pendingBookings: number
  revenueThisMonth: number
  newUsersToday: number
  urgentBookings: number
}

interface RecentActivity {
  id: string
  type: 'user_registration' | 'teacher_application' | 'course_creation' | 'booking_created' | 'booking_confirmed'
  title: string
  description: string
  time: string
  createdAt: string
  isUrgent?: boolean
}

// 简单的SVG图表组件
const SimpleLineChart = ({ data, color = '#3B82F6' }: { data: number[], color?: string }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 100;
  const height = 40;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-10 overflow-visible">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
      {/* 添加渐变填充 */}
      <defs>
        <linearGradient id={`gradient-${color}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polyline
        fill={`url(#gradient-${color})`}
        stroke="none"
        points={`${points} ${width},${height} 0,${height}`}
      />
    </svg>
  );
};

const AdminDashboard = () => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const visibleActivitiesRef = useRef<Set<string>>(new Set())
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalTeachers: 0,
    totalCourses: 0,
    totalBookings: 0,
    userGrowth: 0,
    teacherGrowth: 0,
    courseGrowth: 0,
    bookingGrowth: 0,
    pendingTeacherApplications: 0,
    newBookingsToday: 0,
    pendingBookings: 0,
    revenueThisMonth: 0,
    newUsersToday: 0,
    urgentBookings: 0
  })
  const [loading, setLoading] = useState(true)
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([])
  const [readActivities, setReadActivities] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetchDashboardStats()
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer))
      timersRef.current.clear()
      observerRef.current?.disconnect()
    }
  }, [])

  useEffect(() => {
    if (recentActivities.length > 0) {
      setupIntersectionObserver()
    }
    return () => {
      observerRef.current?.disconnect()
    }
  }, [recentActivities])

  const setupIntersectionObserver = () => {
    observerRef.current?.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const activityId = entry.target.getAttribute('data-activity-id')
          if (!activityId) return

          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            if (!visibleActivitiesRef.current.has(activityId) && !readActivities.has(activityId)) {
              visibleActivitiesRef.current.add(activityId)

              const timer = setTimeout(() => {
                markActivityAsRead(activityId)
              }, 2000)

              timersRef.current.set(activityId, timer)
            }
          } else if (visibleActivitiesRef.current.has(activityId)) {
            visibleActivitiesRef.current.delete(activityId)

            const timer = timersRef.current.get(activityId)
            if (timer) {
              clearTimeout(timer)
              timersRef.current.delete(activityId)
            }
          }
        })
      },
      {
        threshold: [0.7],
        rootMargin: '-50px 0px -50px 0px'
      }
    )

    document.querySelectorAll('[data-activity-id]').forEach(element => {
      observerRef.current?.observe(element)
    })
  }

  const markActivityAsRead = (activityId: string) => {
    setReadActivities(prev => new Set([...prev, activityId]))
  }

  const isNewActivity = (activity: RecentActivity): boolean => {
    const activityTime = new Date(activity.createdAt)
    const now = new Date()
    const hoursDiff = (now.getTime() - activityTime.getTime()) / (1000 * 3600)
    return hoursDiff <= 4 && !readActivities.has(activity.id)
  }

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const statsData = await response.json()
        setStats(statsData)
      } else {
        console.error('获取统计数据失败:', response.status)
        // 保持默认零值状态，不使用模拟数据
      }
      await fetchRecentActivities()
    } catch (error) {
      console.error('获取统计数据失败:', error)
      setRecentActivities([])
    } finally {
      setLoading(false)
    }
  }

  const fetchRecentActivities = async () => {
    try {
      const token = localStorage.getItem('token')

      const [usersResponse, bookingsResponse, teachersResponse] = await Promise.all([
        fetch('/api/admin/users?limit=5', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/bookings/admin?limit=10', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/admin/teacher-applications?limit=5', { headers: { 'Authorization': `Bearer ${token}` } })
      ])

      const activities: RecentActivity[] = []

      if (usersResponse.ok) {
        const users = await usersResponse.json()
        users.slice(0, 3).forEach((user: any) => {
          activities.push({
            id: `user-${user.id}`,
            type: 'user_registration',
            title: '新用户注册',
            description: `${user.firstName} ${user.lastName}注册了账户`,
            time: formatRelativeTime(user.createdAt),
            createdAt: user.createdAt
          })
        })
      }

      if (bookingsResponse.ok) {
        const bookings = await bookingsResponse.json()
        bookings.slice(0, 4).forEach((booking: any) => {
          const activityType = booking.status === 'confirmed' ? 'booking_confirmed' : 'booking_created'
          const title = booking.status === 'confirmed' ? '预约确认' : '新预约'
          const description = `${booking.student?.firstName || '学生'}预约了${booking.course?.title || '课程'}`

          activities.push({
            id: `booking-${booking.id}`,
            type: activityType,
            title,
            description,
            time: formatRelativeTime(booking.createdAt),
            createdAt: booking.createdAt,
            isUrgent: booking.status === 'pending'
          })
        })
      }

      if (teachersResponse.ok) {
        const teachers = await teachersResponse.json()
        teachers.slice(0, 2).forEach((teacher: any) => {
          activities.push({
            id: `teacher-${teacher.id}`,
            type: 'teacher_application',
            title: '教师申请',
            description: `${teacher.user?.firstName || ''}${teacher.user?.lastName || ''}提交了教师申请`,
            time: formatRelativeTime(teacher.createdAt),
            createdAt: teacher.createdAt,
            isUrgent: teacher.status === 'pending'
          })
        })
      }

      activities.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setRecentActivities(activities.slice(0, 8))
    } catch (error) {
      console.error('获取最近活动失败:', error)
      setRecentActivities([])
    }
  }

  const formatRelativeTime = (dateString: string) => {
    const createdAt = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - createdAt.getTime()
    const minutes = Math.floor(diffMs / (1000 * 60))
    const hours = Math.floor(diffMs / (1000 * 3600))
    const days = Math.floor(hours / 24)

    if (minutes < 60) return `${minutes || 1} 分钟前`
    if (hours < 24) return `${hours} 小时前`
    return `${days} 天前`
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_registration':
        return { icon: UsersIcon, color: 'bg-blue-100 text-blue-600' }
      case 'teacher_application':
        return { icon: AcademicCapIcon, color: 'bg-green-100 text-green-600' }
      case 'course_creation':
        return { icon: BookOpenIcon, color: 'bg-yellow-100 text-yellow-600' }
      case 'booking_created':
        return { icon: CalendarIcon, color: 'bg-purple-100 text-purple-600' }
      case 'booking_confirmed':
        return { icon: CheckCircleIcon, color: 'bg-emerald-100 text-emerald-600' }
      default:
        return { icon: BellIcon, color: 'bg-gray-100 text-gray-600' }
    }
  }

  const userGrowthData = [120, 132, 145, 150, 165, 180, 200]
  const revenueData = [15000, 18000, 16500, 21000, 24000, 25800]

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse mb-3"></div>
          <div className="h-5 w-96 bg-gray-100 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
              <div className="h-4 w-24 bg-gray-200 rounded mb-3"></div>
              <div className="h-8 w-20 bg-gray-200 rounded mb-4"></div>
              <div className="h-10 w-full bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 h-64 animate-pulse"></div>
          <div className="bg-white rounded-2xl p-6 h-64 animate-pulse"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* 顶部KPI看板 - 高级版 */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <ChartBarIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">运营数据指挥中心</h1>
              <p className="text-gray-500">实时监控平台运营状况和关键指标</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {/* 总营收卡片 - 带图表 */}
            <div className="group bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-xl hover:border-emerald-200 transition-all">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <CurrencyDollarIcon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">本月总营收</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">¥{(stats.revenueThisMonth / 1000).toFixed(1)}k</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-xs font-semibold text-emerald-600 flex items-center">
                    <ArrowUpIcon className="w-3 h-3 mr-0.5" /> 12.5%
                  </span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-50">
                <SimpleLineChart data={revenueData} color="#10B981" />
              </div>
            </div>

            {/* 用户增长卡片 - 带图表 */}
            <div className="group bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-xl hover:border-blue-200 transition-all">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <UsersIcon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">总用户数</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">{stats.totalUsers}</span>
                  <span className="px-2 py-0.5 rounded-full bg-blue-50 text-xs font-semibold text-blue-600 flex items-center">
                    <ArrowUpIcon className="w-3 h-3 mr-0.5" /> {stats.userGrowth}%
                  </span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-50">
                <SimpleLineChart data={userGrowthData} color="#3B82F6" />
              </div>
            </div>

            {/* 核心指标卡片 3 - 高级渐变 */}
            <div className="relative bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl shadow-lg p-6 text-white overflow-hidden group hover:shadow-2xl hover:shadow-purple-500/25 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white/80 text-sm font-medium">待处理教师申请</p>
                    <p className="text-4xl font-bold mt-2">{stats.pendingTeacherApplications}</p>
                  </div>
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <AcademicCapIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <Link to="/admin/teacher-applications" className="mt-6 inline-flex items-center text-sm font-semibold text-white/90 hover:text-white group-hover:translate-x-1 transition-all">
                  立即审核 <span className="ml-1">&rarr;</span>
                </Link>
              </div>
            </div>

            {/* 核心指标卡片 4 */}
            <div className="group bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-xl hover:border-amber-200 transition-all">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${stats.urgentBookings > 0 ? 'bg-gradient-to-br from-red-500 to-orange-500' : 'bg-gradient-to-br from-amber-500 to-orange-500'}`}>
                      <CalendarIcon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm font-medium text-gray-500">今日课程预约</p>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats.newBookingsToday}</p>
                </div>
              </div>
              {stats.urgentBookings > 0 && (
                <div className="mt-4 px-3 py-2 rounded-xl bg-red-50 border border-red-100">
                  <p className="text-xs font-semibold text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1.5" />
                    {stats.urgentBookings} 个紧急预约需关注
                  </p>
                </div>
              )}
            </div>
          </div>

        {/* 留学业务专区 - 新增 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* 左侧：服务质量监控 */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <StarIcon className="w-5 h-5 text-yellow-500 mr-2" />
              服务质量监控
            </h2>
            <div className="space-y-4">
              {/* 模拟低分预警数据 */}
              {[
                { teacher: '张伟', course: '托福口语冲刺', rating: 2.0, reason: '老师迟到了10分钟', time: '2小时前' },
                { teacher: 'Lisa Wang', course: '文书润色', rating: 3.0, reason: '回复消息太慢', time: '昨天' }
              ].map((alert, idx) => (
                <div key={idx} className="flex items-start p-3 bg-red-50 border border-red-100 rounded-lg">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-red-900">低分预警: {alert.teacher} - {alert.course}</p>
                    <p className="text-xs text-red-700 mt-1">评分: {alert.rating}★ | 原因: {alert.reason}</p>
                  </div>
                  <span className="text-xs text-red-500">{alert.time}</span>
                  <button className="ml-4 text-xs font-medium text-red-600 hover:text-red-800 underline">
                    介入处理
                  </button>
                </div>
              ))}
              
              <div className="border-t border-gray-100 pt-4 mt-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">本周平均评分</span>
                  <span className="font-bold text-gray-900">4.8/5.0</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '96%' }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：留学服务状态 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <GlobeAsiaAustraliaIcon className="w-5 h-5 text-blue-500 mr-2" />
              留学服务概览
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div>
                  <p className="text-sm font-medium text-blue-900">文书服务</p>
                  <p className="text-xs text-blue-700">12 个订单进行中</p>
                </div>
                <span className="text-xl font-bold text-blue-600">12</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-100">
                <div>
                  <p className="text-sm font-medium text-purple-900">选校咨询</p>
                  <p className="text-xs text-purple-700">8 个待分配顾问</p>
                </div>
                <span className="text-xl font-bold text-purple-600">8</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div>
                  <p className="text-sm font-medium text-orange-900">签证辅导</p>
                  <p className="text-xs text-orange-700">5 个即将截止</p>
                </div>
                <span className="text-xl font-bold text-orange-600">5</span>
              </div>
            </div>
          </div>
        </div>

        {/* 最近活动列表 */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="text-lg font-bold text-gray-900">全站实时动态</h2>
            <Link to="/admin/logs" className="text-sm text-blue-600 hover:text-blue-800">查看所有日志</Link>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {recentActivities.map((activity) => {
                const isNew = isNewActivity(activity)
                const { icon: IconComponent, color } = getActivityIcon(activity.type)
                
                return (
                  <div
                    key={activity.id}
                    data-activity-id={activity.id}
                    className={`flex items-center p-3 rounded-lg transition-all duration-200 ${
                      isNew 
                        ? 'bg-blue-50/50 border border-blue-100' 
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className={`h-10 w-10 rounded-full ${color} flex items-center justify-center bg-opacity-20`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-gray-900">
                          {activity.title}
                        </p>
                        {isNew && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">{activity.description}</p>
                      <p className="text-xs text-gray-400 mt-1 flex items-center">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        {activity.time}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
